import api from './api';
import { Transaction, TransactionResponseDTO } from '../types';
import {
  CreateExpenseRequest,
  CreateTransferRequest,
  DailyBalanceDTO,
  DailyBalanceParams,
  TransactionIdResponse,
} from '../types';

/**
 * Servicio para manejar el historial de transacciones y nuevas operaciones
 * Refactorizado para Sprint 4 - Sistema de Transacciones
 */
export const transactionService = {
  /**
   * Obtener todas las transacciones de todos los tipos (LEGACY)
   * NOTA: Este endpoint usa el sistema legacy (/transaction-history)
   * @deprecated Usar getAuditTransactions() para el nuevo sistema
   */
  getAllTransactions: async (): Promise<Transaction[]> => {
    const response = await api.get<Transaction[]>('/transaction-history');
    return response.data;
  },

  /**
   * Obtener auditoría completa de transacciones
   * GET /api/transactions
   *
   * Devuelve todas las transacciones del sistema ordenadas por fecha (descendente)
   * Incluye información detallada de entradas contables y links a entidades relacionadas
   *
   * @returns Lista completa de transacciones con detalles
   */
  getAuditTransactions: async (): Promise<TransactionResponseDTO[]> => {
    try {
      const response = await api.get<any>('/transactions');
      // El backend envuelve la respuesta: { success, data, ... }
      const raw = response.data;
      return Array.isArray(raw)
        ? raw
        : Array.isArray(raw?.data)
          ? raw.data
          : [];
    } catch (error) {
      console.error('Error fetching audit transactions:', error);
      throw error;
    }
  },

  /**
   * Crear un egreso de caja (gasto)
   * POST /api/transactions/expense
   *
   * @param payload - Datos del egreso (caja, monto, descripción)
   * @returns ID de la transacción creada
   */
  createExpense: async (payload: CreateExpenseRequest): Promise<number> => {
    try {
      const response = await api.post<TransactionIdResponse>(
        '/transactions/expense',
        payload,
      );
      return response.data.data;
    } catch (error) {
      console.error('Error creating expense transaction:', error);
      throw error;
    }
  },

  /**
   * Crear un ingreso a caja (manual/externo)
   * POST /api/transactions/income
   *
   * @param payload - Datos del ingreso (caja, monto, descripción)
   * @returns ID de la transacción creada
   */
  createIncome: async (payload: CreateExpenseRequest): Promise<number> => {
    try {
      const response = await api.post<TransactionIdResponse>(
        '/transactions/income',
        payload,
      );
      return response.data.data;
    } catch (error) {
      console.error('Error creating income transaction:', error);
      throw error;
    }
  },

  /**
   * Crear una transferencia interna entre cajas
   * POST /api/transactions/transfer
   *
   * Las cajas deben tener la misma moneda
   *
   * @param payload - Datos de la transferencia (caja origen, destino, monto)
   * @returns ID de la transacción creada
   * @throws Error si las monedas no coinciden o saldo insuficiente
   */
  createTransfer: async (payload: CreateTransferRequest): Promise<number> => {
    try {
      const response = await api.post<TransactionIdResponse>(
        '/transactions/transfer',
        payload,
      );
      return response.data.data;
    } catch (error) {
      console.error('Error creating transfer transaction:', error);
      throw error;
    }
  },

  /**
   * Obtener reporte de balance diario para una caja
   * GET /api/reports/daily-balance
   *
   * @param params - Parámetros de consulta (caja, rango de fechas)
   * @returns Lista de balances diarios con débitos, créditos y cambio neto
   */
  getDailyBalance: async (
    params: DailyBalanceParams,
  ): Promise<DailyBalanceDTO[]> => {
    try {
      const response = await api.get<DailyBalanceDTO[]>(
        '/reports/daily-balance',
        {
          params: {
            cashBoxId: params.cashBoxId,
            startDate: params.startDate,
            endDate: params.endDate,
          },
        },
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching daily balance report:', error);
      throw error;
    }
  },

  /**
   * Obtener movimientos detallados de una caja
   * GET /api/reports/cashbox-movements
   */
  getCashBoxMovements: async (
    params: { cashBoxId: number; startDate: string; endDate: string },
  ): Promise<import('../types').ReportMovementDTO[]> => {
    try {
      const response = await api.get<import('../types').ReportMovementDTO[]>(
        '/reports/cashbox-movements',
        {
          params: {
            cashBoxId: params.cashBoxId,
            startDate: params.startDate,
            endDate: params.endDate,
          },
        },
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching cashbox movements:', error);
      throw error;
    }
  },
};
